import { AxiosResponse } from 'axios';
import moment from 'moment';
import { FaRegCommentDots } from 'react-icons/fa';
import { FaRegMoneyBill1 } from 'react-icons/fa6';
import { GiGps } from 'react-icons/gi';
import { MdOutlineLocalTaxi } from 'react-icons/md';
import { PiSquaresFourBold } from 'react-icons/pi';

import { FieldTypes, IDynamicFields, ValidationDataType } from 'components/DynamicField/DynamicField.model';
import {
  IDisableIntegrationResponse,
  IEnableIntegrationResponse,
  IGetIntegrationsApp,
  IGetIntegrationsCategory,
  IGetIntegrationsResponse,
  IGetIntegrationsSyncingStatus,
  IGetSingleIntegrationsApp,
  IGetSingleIntegrationsResponse,
  IIntegrationsRunActionResponse,
  IIntegrationsValidation,
} from 'models/http/integrations';
import { IntegrationIDs, IntegrationsCategory } from 'models/integrations';
import { HttpTimeoutPriority } from 'utils/enums/http-timeout-priority';
import { serverAxios } from 'utils/http';

class IntegrationsHttp {
  private readonly route = '/integrations';

  public async get(abortController?: AbortController): Promise<{
    integrationsApps: IGetIntegrationsApp[];
    integrationsCategories: IGetIntegrationsCategory[];
    integrationsSyncingStatus?: IGetIntegrationsSyncingStatus;
  }> {
    return serverAxios
      .get(this.route, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetIntegrationsResponse>) => {
        type SingleIntegrationResponse = IGetIntegrationsResponse['integrations'][number];

        //will be remove after back imlementation
        const integrationsCategories = [
          {
            label: 'All',
            category: IntegrationsCategory.All,
            img: 'Routing Settings',
            icon: PiSquaresFourBold,
          },
          {
            label: 'Communications',
            category: IntegrationsCategory.Communications,
            img: 'Routing Settings',
            icon: FaRegCommentDots,
          },
          {
            label: 'Dispatch',
            category: IntegrationsCategory.Dispatch,
            img: 'Routing Settings',
            icon: MdOutlineLocalTaxi,
          },
          {
            label: 'Finance',
            category: IntegrationsCategory.Finance,
            img: 'Routing Settings',
            icon: FaRegMoneyBill1,
          },
          {
            label: 'Vehicle Integration',
            category: IntegrationsCategory.VehicleIntegration,
            img: 'Routing Settings',
            icon: GiGps,
          },
        ];

        const integrationsApps: IGetIntegrationsApp[] = [
          {
            id: 'sendgrid',
            name: 'SendGrid',
            integrated: false,
            category: IntegrationsCategory.Communications,
            logo: '/images/integrations/SendGrid_2016_Logo.png',
            outOfService: true,
          },
          {
            backgroundColor: '#fff',
            name: 'Renault',
            id: 'renault',
            category: IntegrationsCategory.VehicleIntegration,
            logo: '/images/integrations/renaultBlack.png',
            integrated: false,
            outOfService: true,
          },
          {
            backgroundColor: '#6ea3cf',
            name: 'Optibus',
            id: IntegrationIDs.Optibus,
            category: IntegrationsCategory.VehicleIntegration,
            logo: '/images/integrations/optibus.png',
            integrated: response.data.integrations.some(
              (integration: SingleIntegrationResponse) => integration.integrationId === IntegrationIDs.Optibus,
            ),
          },
          {
            backgroundColor: '#03a300',
            name: 'iCabbi',
            id: IntegrationIDs.ICabbi,
            category: IntegrationsCategory.Dispatch,
            logo: '/images/integrations/iCabbi.png',
            integrated: response.data.integrations.some(
              (integration: SingleIntegrationResponse) => integration.integrationId === IntegrationIDs.ICabbi,
            ),
          },
          {
            id: 'twilio',
            backgroundColor: '#ca0201',
            name: 'Twilio',
            outOfService: true,
            category: IntegrationsCategory.Communications,
            logo: '/images/integrations/twilio-logo.webp',
            integrated: false,
          },
          {
            id: 'podium',
            name: 'Podium',
            outOfService: true,
            category: IntegrationsCategory.Communications,
            logo: '/images/integrations/Podium-Logo-New.png',
            integrated: false,
          },
          {
            id: 'stripe',
            backgroundColor: '#665aff',
            name: 'Stripe',
            outOfService: true,
            category: IntegrationsCategory.Finance,
            logo: '/images/integrations/Stripe-Symbol.png',
            integrated: false,
          },
        ];

        const integrationsSyncingStatus = response.data.integrations.reduce<Record<string, number> | undefined>(
          (acc, { integrationId, syncingPercentage }: SingleIntegrationResponse) =>
            typeof syncingPercentage === 'number' ? { ...acc, [integrationId]: syncingPercentage } : acc,
          undefined,
        );

        return { integrationsApps, integrationsCategories, integrationsSyncingStatus };
      });
  }

  public async getById(
    id: string,
    abortController?: AbortController,
  ): Promise<{
    actions: { label: string; id: string }[];
    enabled: boolean;
    integrationsApp: IGetSingleIntegrationsApp;
    validation: IIntegrationsValidation;
    fields: {
      auth: IDynamicFields[];
      settings: IDynamicFields[];
    };
  }> {
    //TODO: TEMP MOCK DATA WILL BE REMOVE AFTER SERVER IMPLEMENTATION
    return serverAxios
      .get(`${this.route}/single/${id}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetSingleIntegrationsResponse>) => {
        //TODO: TEMP MOCK DATA WILL BE REMOVE AFTER SERVER IMPLEMENTATION

        if (id === IntegrationIDs.ICabbi) {
          const icabbiResponse: {
            actions: { label: string; id: string }[];
            enabled: boolean;
            integrationsApp: IGetSingleIntegrationsApp;
            validation: IIntegrationsValidation;
            fields: {
              auth: IDynamicFields[];
              settings: IDynamicFields[];
            };
          } = {
            enabled: response.data.enabled,
            actions: [
              {
                label: 'Sync Data',
                id: 'icabbi-sync-data',
              },
              {
                label: 'Sync Hooks',
                id: 'icabbi-sync-hooks',
              },
            ],
            integrationsApp: {
              backgroundColor: '#03a300',
              name: 'iCabbi',
              id: IntegrationIDs.ICabbi,
              category: IntegrationsCategory.Dispatch,
              logo: '/images/integrations/iCabbi.png',
              miniLogo: '/images/integrations/iCabbiMiniLogo.png',
              header: 'Dispatching with iCabbi',
              shortDescription:
                'iCabbi provides cloud-based taxi dispatch systems that integrate seamlessly with websites, booking engines, and mobile booking apps.',
              description:
                'iCabbi provides cloud-based taxi dispatch systems that integrate seamlessly with websites, booking engines, and mobile booking apps.',
            },
            fields: {
              auth: [
                {
                  controller: 'apiURL',
                  labelAbove: 'iCabbi API URL',
                  fieldType: FieldTypes.Text,
                  info: 'Please enter the full API URL, for exmaple: https://9stagingapi.icabbi.com/9staging',
                  order: 4,
                },
                {
                  controller: 'clientId',
                  labelAbove: 'App Key',
                  fieldType: FieldTypes.Text,
                  info: 'Make sure credentials have access enabled for the following: Journeys, Hooks, Bookings, Drivers, Vehicles, Accounts and Attributes',
                  order: 5,
                },
                {
                  controller: 'clientSecret',
                  labelAbove: 'Secret Key',
                  fieldType: FieldTypes.Text,
                  info: 'Make sure credentials have access enabled for the following: Journeys, Hooks, Bookings, Drivers, Vehicles, Accounts and Attributes',
                  order: 6,
                },
              ],
              settings: [
                {
                  controller: 'integrationName',
                  labelAbove: 'Integration Name',
                  fieldType: FieldTypes.Text,
                  info: 'The integration name',
                  ...(response?.data?.settings?.['integrationName'] ? { value: response.data.settings['integrationName'] } : {}),
                  editable: true,
                  order: 1,
                },
                {
                  controller: 'companyId',
                  labelAbove: 'Company ID',
                  fieldType: FieldTypes.Text,
                  info: 'The integration company ID',
                  ...(response?.data?.settings?.['companyId'] ? { value: response.data.settings['companyId'] } : {}),
                  editable: true,
                  order: 2,
                },
                {
                  controller: 'timezone',
                  fieldType: FieldTypes.Dropdown,
                  ...(response?.data?.settings?.['timezone'] ? { value: response.data.settings['timezone'] } : ''),
                  options: moment.tz.names().map((name: string) => {
                    return { label: name, value: name };
                  }),
                  labelAbove: 'Time Zone',
                  info: "The system's time zone",
                  order: 3,
                },
                {
                  controller: 'siteId',
                  labelAbove: 'Site ID',
                  fieldType: FieldTypes.Number,
                  value: response.data.settings?.siteId,
                  info: 'Leave empty if you wish to sync your whole dispatch',
                  order: 7,
                },
                {
                  controller: 'disablePushUpdates',
                  fieldType: FieldTypes.Switch,
                  value: !!response.data.settings?.disablePushUpdates,
                  confirmSwitchChanges: response.data.enabled && {
                    translationKeys: {
                      toDisabledInstructions: 'integrations.app.actions.oneTwoWayVerificationModal.toTwoWayInstructions',
                      toEnabledInstructions: 'integrations.app.actions.oneTwoWayVerificationModal.toOneWayInstructions',
                      verification: 'integrations.app.actions.oneTwoWayVerificationModal.verificationPhrase',
                    },
                  },
                  editable: true,
                  labelAfter: 'One Way',
                  labelBefore: 'Two Way',
                  info: 'When two way is enabled, Moovex will create journeys & might modify bookings on your iCabbi account',
                  order: 8,
                },
              ],
            },
            validation: {
              apiURL: {
                dataType: ValidationDataType.String,
                isRequired: true,
                defaultValue: '',
                controller: 'apiURL',
              },
              timezone: {
                dataType: ValidationDataType.Object,
                isRequired: true,
                defaultValue: null,
                controller: 'timezone',
              },
              integrationName: {
                dataType: ValidationDataType.String,
                isRequired: true,
                defaultValue: '',
                controller: 'integrationName',
              },
              companyId: {
                dataType: ValidationDataType.String,
                isRequired: true,
                defaultValue: '',
                controller: 'companyId',
              },
              clientId: {
                dataType: ValidationDataType.String,
                isRequired: true,
                defaultValue: '',
                controller: 'clientId',
              },
              clientSecret: {
                dataType: ValidationDataType.String,
                isRequired: true,
                defaultValue: '',
                controller: 'clientSecret',
              },
              siteId: {
                dataType: ValidationDataType.Number,
                isRequired: false,
                defaultValue: '',
                controller: 'siteId',
              },
              disablePushUpdates: {
                dataType: ValidationDataType.Boolean,
                isRequired: true,
                defaultValue: false,
                controller: 'disablePushUpdates',
              },
            },
          };

          return icabbiResponse;
        }

        return {
          enabled: response.data.enabled,
          actions: [],
          integrationsApp: {
            backgroundColor: '#6ea3cf',
            name: 'Optibus',
            id: IntegrationIDs.Optibus,
            category: IntegrationsCategory.Dispatch,
            logo: '/images/integrations/optibus.png',
            miniLogo: '/images/integrations/optibus.png',
            header: 'Scheduling with Optibus',
            shortDescription: "Connect your Optibus account to easily send trips directly to your Optibus's projects.",
            description: "Connect your Optibus account to easily send trips directly to your Optibus's projects.",
          },
          fields: {
            auth: [
              {
                controller: 'apiURL',
                labelAbove: 'API Url',
                fieldType: FieldTypes.Text,
                info: 'API url associated with your Optibus account',
              },
              {
                controller: 'token',
                labelAbove: 'Token',
                fieldType: FieldTypes.Text,
                info: 'Secret token for your Optibus account',
              },
            ],
            settings: [],
          },
          validation: {
            apiURL: {
              dataType: ValidationDataType.String,
              isRequired: true,
              controller: 'apiURL',
            },
            token: {
              dataType: ValidationDataType.String,
              isRequired: true,
              controller: 'token',
            },
          },
        };
      });
  }

  public async update(name: string, settings?: Record<string, unknown>): Promise<IEnableIntegrationResponse> {
    return serverAxios
      .patch<IEnableIntegrationResponse>(`${this.route}/${name}/update`, {
        settings,
      })
      .then((response: AxiosResponse<IEnableIntegrationResponse>) => response.data);
  }

  public async enable(
    name: string,
    authConfig?: Record<string, unknown>,
    settings?: Record<string, unknown>,
  ): Promise<IEnableIntegrationResponse> {
    return serverAxios
      .post<IEnableIntegrationResponse>(`${this.route}/${name}/enable`, {
        authConfig,
        settings,
      })
      .then((response: AxiosResponse<IEnableIntegrationResponse>) => response.data);
  }

  public async disable(name: string): Promise<IDisableIntegrationResponse> {
    return serverAxios
      .post<IDisableIntegrationResponse>(`${this.route}/${name}/disable`, undefined, { timeout: HttpTimeoutPriority.Highest })
      .then((response: AxiosResponse<IDisableIntegrationResponse>) => response.data);
  }

  public async runAction(name: string, action: string): Promise<IIntegrationsRunActionResponse> {
    return serverAxios
      .post<IIntegrationsRunActionResponse>(`${this.route}/${name}/action/${action}`)
      .then((response: AxiosResponse<IIntegrationsRunActionResponse>) => response.data);
  }
}

export default IntegrationsHttp;
